import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { supabase } from '@/integrations/supabase/client';
import { captureAttribution } from '@/lib/attribution';

let visitorIdCache: string | null = null;

export const getVisitorId = () => visitorIdCache;

export const useVisitorTracking = () => {
  const location = useLocation();
  const ipDataRef = useRef<{ org: string | null; country: string | null }>({ org: null, country: null });
  const initializedRef = useRef(false);

  useEffect(() => {
    // Persist campaign attribution as soon as the visitor lands, on any route.
    captureAttribution();

    const init = async () => {
      if (!visitorIdCache) {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        visitorIdCache = result.visitorId;
      }

      // Resolve IP info once per session
      if (!initializedRef.current) {
        initializedRef.current = true;
        try {
          const { data } = await supabase.functions.invoke('resolve-visitor');
          if (data) {
            ipDataRef.current = { org: data.org || null, country: data.country || null };
          }
        } catch {
          // silently fail
        }
      }

      // Log page view
      supabase.from('page_views').insert({
        visitor_id: visitorIdCache,
        page_path: location.pathname + location.search,
        referrer: document.referrer || null,
        user_agent: navigator.userAgent,
        ip_org: ipDataRef.current.org,
        ip_country: ipDataRef.current.country,
      }).then(() => {});
    };

    // Defer to idle time: FingerprintJS reads layout/geometry, which forces
    // reflows if run during initial render.
    const ric: typeof requestIdleCallback | undefined =
      typeof window !== 'undefined' ? (window as any).requestIdleCallback : undefined;

    let idleId: number | undefined;
    let timeoutId: number | undefined;

    if (ric) {
      idleId = ric(() => void init(), { timeout: 3000 });
    } else {
      timeoutId = window.setTimeout(() => void init(), 1500);
    }

    return () => {
      if (idleId !== undefined && (window as any).cancelIdleCallback) {
        (window as any).cancelIdleCallback(idleId);
      }
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [location.pathname, location.search]);
};

